import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/features/auth/useAuth';

const STORAGE_PREFIX = 'kreart:table-column-order';

export interface OrderedColumn {
  key: string;
}

interface ColumnOrderState {
  storageKey: string;
  signature: string;
  order: string[];
}

export function usePersistedColumnOrder<TColumn extends OrderedColumn>(
  tableId: string,
  columns: readonly TColumn[],
) {
  const { user } = useAuth();
  const userScope = user?.id ?? 'anonymous';
  const storageKey = `${STORAGE_PREFIX}:${userScope}:${tableId}`;
  const signature = columns.map((column) => column.key).join('|');
  const defaultOrder = useMemo(() => columns.map((column) => column.key), [signature]);

  const [state, setState] = useState<ColumnOrderState>(() => ({
    storageKey,
    signature,
    order: readStoredOrder(storageKey, defaultOrder),
  }));

  useEffect(() => {
    setState({
      storageKey,
      signature,
      order: readStoredOrder(storageKey, defaultOrder),
    });
  }, [defaultOrder, signature, storageKey]);

  useEffect(() => {
    if (state.storageKey !== storageKey || state.signature !== signature) return;
    writeStoredOrder(storageKey, state.order);
  }, [signature, state, storageKey]);

  const orderedColumns = useMemo(() => {
    const byKey = new Map(columns.map((column) => [column.key, column]));
    return state.order.flatMap((key) => {
      const column = byKey.get(key);
      return column ? [column] : [];
    });
  }, [columns, state.order]);

  const moveColumn = (key: string, direction: 'left' | 'right') => {
    setState((current) => {
      const index = current.order.indexOf(key);
      if (index < 0) return current;
      const nextIndex = direction === 'left' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= current.order.length) return current;
      const nextOrder = [...current.order];
      const currentKey = nextOrder[index];
      const nextKey = nextOrder[nextIndex];
      if (currentKey === undefined || nextKey === undefined) return current;
      nextOrder[index] = nextKey;
      nextOrder[nextIndex] = currentKey;
      return { ...current, order: nextOrder };
    });
  };

  const resetColumnOrder = () => {
    setState((current) => ({ ...current, order: defaultOrder }));
  };

  return {
    orderedColumns,
    columnOrder: state.order,
    moveColumn,
    resetColumnOrder,
  };
}

function normalizeOrder(storedOrder: readonly string[], defaultOrder: readonly string[]): string[] {
  const defaultKeys = new Set(defaultOrder);
  const result = storedOrder.filter((key) => defaultKeys.has(key));
  const present = new Set(result);
  // Insert any keys missing from the stored order at their natural default
  // position (right after their nearest preceding default neighbor) instead of
  // appending them at the end — so newly added columns appear where defined.
  defaultOrder.forEach((key, defaultIndex) => {
    if (present.has(key)) return;
    let insertAt = 0;
    for (let i = defaultIndex - 1; i >= 0; i -= 1) {
      const prev = defaultOrder[i];
      if (prev === undefined) continue;
      const idx = result.indexOf(prev);
      if (idx >= 0) {
        insertAt = idx + 1;
        break;
      }
    }
    result.splice(insertAt, 0, key);
    present.add(key);
  });
  return result;
}

function readStoredOrder(storageKey: string, defaultOrder: readonly string[]): string[] {
  if (typeof window === 'undefined') return [...defaultOrder];

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [...defaultOrder];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.every((value) => typeof value === 'string')) {
      console.warn(`Invalid table column order stored for ${storageKey}.`);
      return [...defaultOrder];
    }
    return normalizeOrder(parsed, defaultOrder);
  } catch (error) {
    console.warn(`Could not read table column order for ${storageKey}.`, error);
    return [...defaultOrder];
  }
}

function writeStoredOrder(storageKey: string, order: readonly string[]): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(order));
  } catch (error) {
    console.warn(`Could not persist table column order for ${storageKey}.`, error);
  }
}
